import { memo, useCallback } from 'react'
import { Box, Text, Group, Badge, Checkbox } from 'components'
import { useTranslation } from 'react-i18next'
import { ILvl4Item } from './types'

interface BrowseTreeSelectableItemProps {
  item: ILvl4Item
  isSelected: boolean
  isAlreadyAdded: boolean
  onToggle: (item: ILvl4Item) => void
}

export const BrowseTreeSelectableItem = memo(({
  item,
  isSelected,
  isAlreadyAdded,
  onToggle,
}: BrowseTreeSelectableItemProps) => {
  const { t } = useTranslation()

  const handleClick = useCallback(() => {
    if (!isAlreadyAdded) {
      onToggle(item)
    }
  }, [isAlreadyAdded, onToggle, item])

  const handleCheckboxChange = useCallback(() => {
    if (!isAlreadyAdded) {
      onToggle(item)
    }
  }, [isAlreadyAdded, onToggle, item])

  return (
    <Box
      onClick={handleClick}
      sx={theme => ({
        padding: '16px 20px',
        borderRadius: theme.radius.md,
        border: `1px solid ${
          isSelected
            ? theme.colors.teal[4]
            : theme.colorScheme === 'dark'
              ? theme.colors.dark[4]
              : theme.colors.gray[3]
        }`,
        backgroundColor: isSelected
          ? theme.colorScheme === 'dark'
            ? theme.fn.rgba(theme.colors.teal[9], 0.15)
            : theme.colors.teal[0]
          : theme.colorScheme === 'dark'
            ? theme.colors.dark[6]
            : theme.white,
        boxShadow: theme.shadows.sm,
        cursor: isAlreadyAdded ? 'default' : 'pointer',
        opacity: isAlreadyAdded ? 0.6 : 1,
        transition: 'all 0.2s ease',
        '&:hover': !isAlreadyAdded ? {
          boxShadow: theme.shadows.md,
          borderColor: isSelected
            ? theme.colors.teal[4]
            : theme.colors.gray[4],
          backgroundColor: isSelected
            ? theme.colors.teal[0]
            : theme.colors.gray[0],
        } : undefined,
      })}
    >
      <Group align="flex-start" noWrap spacing={16}>
        <Checkbox
          checked={isSelected || isAlreadyAdded}
          disabled={isAlreadyAdded}
          onChange={handleCheckboxChange}
          onClick={e => e.stopPropagation()}
          color="teal"
          size="md"
          radius="sm"
          styles={theme => ({
            input: {
              cursor: isAlreadyAdded ? 'default' : 'pointer',
              backgroundColor: isSelected ? theme.colors.teal[6] : undefined,
              borderColor: isSelected ? theme.colors.teal[6] : theme.colors.gray[4],
            },
          })}
        />
        <Box sx={{ flex: 1, minWidth: 0, paddingTop: 2 }}>
          <Text size="sm" sx={{ lineHeight: 1.5 }}>
            {item.originalCodes && (
              <Text component="span" weight={700}>
                {item.originalCodes}
              </Text>
            )}
            {item.originalCodes && ' - '}
            {item.name}
          </Text>
          {isAlreadyAdded && (
            <Badge size="xs" color="gray" variant="light" mt={8} radius="sm">
              {t('arc_already_added')}
            </Badge>
          )}
        </Box>
      </Group>
    </Box>
  )
})

BrowseTreeSelectableItem.displayName = 'BrowseTreeSelectableItem'
